import { DataPicker, Input, Radio, Textarea, Typography } from '@/ui-kit/';
import { FieldBlock } from '@/components/field-block';
import styles from './about-and-travel-plans-registartion-form.module.scss';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { debounce, throttle } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { type Profile } from '@/types/profile';
import { ButtonSubmitStep } from '../button-submit-step';
import { eventsApi } from '@/api';
import { callYaMetrika } from '@/utils/call-ya-metrika';
import { useToast } from '@/store/useToast';
import { SuccessNotificationIcon } from '@/assets/icons';
import { registrationStepAboutAndTravelPlansSchema, RegistrationStepAboutAndTravelPlansType } from '@/schemes/registration-step-about-and-travel-plans-schema';
import { useTranslation } from 'react-i18next';

interface Props {
    userData?: Profile | null;
    onUpdateUserData: (data: RegistrationStepAboutAndTravelPlansType) => Promise<any>;
    nextStep: () => void;
}

export const AboutAndTravelPlansRegistartionForm: React.FC<Props> = ({ userData, onUpdateUserData, nextStep }) => {
    const showToast = useToast((state) => state.show);
    useEffect(() => {
        callYaMetrika('new_aboutme_screen');
        eventsApi.updateUserEvent('new_aboutme_screen');
        eventsApi.updateUserEvent('registration_step_view_new_aboutme_screen')
    }, []);

    const initialValues = useMemo(() => {
        return {
            about_me: userData?.about_me ?? '',
            travel_plans: userData?.travel_plans ?? '',
        };
    }, [userData]);

    const [isLoading, setIsLoading] = useState(false);

    const methods = useForm<RegistrationStepAboutAndTravelPlansType>({
        resolver: zodResolver(registrationStepAboutAndTravelPlansSchema),
        defaultValues: initialValues,
    });
    const {
        register,
        handleSubmit,
        formState: { errors, isDirty, isValid },
        watch,
    } = methods;

    const onSubmit = async (data: RegistrationStepAboutAndTravelPlansType) => {
        try {
            setIsLoading(true);
            /*
             * for update only dirty data use code below
             * if (isDirty) {
             *     await onUpdateUserData(data);
             * }
             */
            await onUpdateUserData(data);
            nextStep();
            eventsApi.updateUserEvent('registration_step_complete_new_aboutme_screen')
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    const goalTravelPlansFieldChange = useCallback(
        throttle(() => {
            callYaMetrika('your_travel_dreams');
            eventsApi.updateUserEvent('registration_travel_plans');
        }, 2000),
        [],
    );

    const goalAboutMeFieldChange = useCallback(
        throttle(() => {
            callYaMetrika('about_me');
            eventsApi.updateUserEvent('registration_about_me');
        }, 2000),
        [],
    );
    const aboutMe = watch('about_me');
    const travelPlans = watch('travel_plans');

    useEffect(() => {
        if (aboutMe !== initialValues.about_me) {
            goalAboutMeFieldChange();
        }
    }, [aboutMe]);

    useEffect(() => {
        if (travelPlans !== initialValues.travel_plans) {
            goalTravelPlansFieldChange();
        }
    }, [travelPlans]);

    const { t } = useTranslation();

    return (
        <FormProvider {...methods}>
            <form
                className={styles.form}
                onSubmit={handleSubmit(onSubmit)}>
                <div className={styles.formContainer}>
                    <div className={styles.formContainerHeader}>
                        <Typography
                            variant="p"
                            fontWeight="medium"
                            className={styles.titleForm}
                            center>
                            {t('registration.tell_about_yourself')}
                        </Typography>
                        <Typography
                            variant="p"
                            fontWeight="medium"
                            className={styles.subtitleForm}
                            center>
                            {t('registration.about_subtitle')}
                        </Typography>
                    </div>
                    <FieldBlock error={!!errors?.about_me?.message}>
                        <Textarea
                            label={t('about_secondary')}
                            required
                            maxLength={250}
                            {...register('about_me', { required: true })}
                            defaultValue={userData?.about_me}
                            placeholder={t('about_me_placeholder')}
                            error={errors?.about_me?.message} />
                    </FieldBlock>

                    <FieldBlock id="travel_plans_field" error={!!errors?.travel_plans?.message}>
                        <Textarea
                            label={t('travel_plans_label')}
                            maxLength={500}
                            placeholder={t('travel_plans_placeholder')}
                            defaultValue={initialValues.travel_plans ?? ''}
                            {...register('travel_plans', { required: true })} />
                    </FieldBlock>
                </div>
                <ButtonSubmitStep
                    isLoading={isLoading}
                    stepNumber={1}
                    isDisabled={!isValid} />
            </form>
        </FormProvider>
    );
};
